/**
 * SunTimesOverlay Component
 * Visual overlay showing VFR daylight hours (sunrise to sunset)
 * Displays times in user's timezone, not airport's local timezone
 */

import React from 'react';
import dayjs from 'dayjs';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import type { SunTimesDay } from '@/api/types/calendars.types';
import './SunTimesOverlay.css';

interface SunTimesOverlayProps {
  /** Sun times data for the current date/airport */
  sunTimes: SunTimesDay;
  /** Optional: show civil twilight instead of just sunrise/sunset */
  showTwilight?: boolean;
}

const HOUR_HEIGHT = 60; // pixels per hour (must match calendar view)

/**
 * Calculate position and height for the daylight overlay
 * Based on 24-hour grid starting at midnight (00:00)
 * Converts UTC times to user's timezone for display
 */
const calculateDaylightPosition = (
  sunTimes: SunTimesDay,
  showTwilight: boolean,
  parse: (date: string) => dayjs.Dayjs,
  timezone: string,
  dateString: string
): { top: number; height: number } => {
  // Use civil twilight for VFR planning, or sunrise/sunset for visual daylight
  // Parse UTC times and convert to user's timezone
  const startTime = showTwilight
    ? parse(sunTimes.civil_twilight_begin)
    : parse(sunTimes.sunrise);
  const endTime = showTwilight
    ? parse(sunTimes.civil_twilight_end)
    : parse(sunTimes.sunset);

  // Create midnight in user timezone for the same date
  const dayStartInUserTz = dayjs.tz(dateString, timezone).startOf('day');

  // Calculate hours from midnight in user timezone
  const startHour = startTime.diff(dayStartInUserTz, 'minute') / 60;
  const endHour = endTime.diff(dayStartInUserTz, 'minute') / 60;

  // Calculate position from top (midnight = 0)
  const top = startHour * HOUR_HEIGHT;
  const height = (endHour - startHour) * HOUR_HEIGHT;

  return { top, height };
};

export const SunTimesOverlay: React.FC<SunTimesOverlayProps> = ({
  sunTimes,
  showTwilight = true, // Default to civil twilight for VFR
}) => {
  const { formatTime, parse, timezone } = useUserTimezone();
  const dateString = sunTimes.date; // YYYY-MM-DD format

  const { top, height } = calculateDaylightPosition(
    sunTimes,
    showTwilight,
    parse,
    timezone,
    dateString
  );

  // Convert UTC times to user's timezone for display
  const sunriseTime = formatTime(sunTimes.sunrise, 'HH:mm');
  const sunsetTime = formatTime(sunTimes.sunset, 'HH:mm');
  const civilTwilightBegin = formatTime(sunTimes.civil_twilight_begin, 'HH:mm');
  const civilTwilightEnd = formatTime(sunTimes.civil_twilight_end, 'HH:mm');

  // Calculate marker positions in user timezone
  const sunriseInUserTz = parse(sunTimes.sunrise);
  const sunsetInUserTz = parse(sunTimes.sunset);
  const civilBeginInUserTz = parse(sunTimes.civil_twilight_begin);
  const civilEndInUserTz = parse(sunTimes.civil_twilight_end);

  // Calculate offset from civil twilight to sunrise/sunset (in minutes)
  const sunriseOffsetMinutes = showTwilight
    ? sunriseInUserTz.diff(civilBeginInUserTz, 'minute')
    : 0;
  const sunsetOffsetMinutes = showTwilight
    ? civilEndInUserTz.diff(sunsetInUserTz, 'minute')
    : 0;

  return (
    <>
      {/* Daylight hours overlay */}
      <div
        className="sun-times-overlay"
        style={{
          top: `${top}px`,
          height: `${height}px`,
        }}
        title={
          showTwilight
            ? `VFR Daylight: ${civilTwilightBegin} - ${civilTwilightEnd}`
            : `Daylight: ${sunriseTime} - ${sunsetTime}`
        }
      >
        {/* Sunrise marker */}
        <div
          className="sun-times-marker sunrise"
          style={{
            top: showTwilight
              ? `${(sunriseOffsetMinutes / 60) * HOUR_HEIGHT}px`
              : '0px',
          }}
        >
          <span className="marker-icon">☀️</span>
          <span className="marker-time">{sunriseTime}</span>
        </div>

        {/* Sunset marker */}
        <div
          className="sun-times-marker sunset"
          style={{
            bottom: showTwilight
              ? `${(sunsetOffsetMinutes / 60) * HOUR_HEIGHT}px`
              : '0px',
          }}
        >
          <span className="marker-icon">🌙</span>
          <span className="marker-time">{sunsetTime}</span>
        </div>

        {/* Day length display */}
        <div className="sun-times-info">
          <div className="day-length">
            {Math.floor(sunTimes.day_length / 3600)}h{' '}
            {Math.floor((sunTimes.day_length % 3600) / 60)}m
          </div>
        </div>
      </div>
    </>
  );
};
